import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from '../context/AuthContext';
import 'animate.css';
import PagosView from "./PagosView";

function safeParse(value, fallback) {
  try {
    if (typeof value === "string" && value.trim() !== "") return JSON.parse(value);
    return value ?? fallback;
  } catch (err) {
    return fallback;
  }
}

function PedidoCard({ 
  pedido = {}, 
  pagoPendienteGlobal = null,
  onPagoConfirmado = null 
}) {
  const { user } = useContext(AuthContext);
  const [precioFinal, setPrecioFinal] = useState("");
  const [pagoPendiente, setPagoPendiente] = useState(null);

  // 🎯 Sincronización segura de productos
  const productos = useMemo(() => {
    if (!pedido) return [];
    if (Array.isArray(pedido.productos)) return pedido.productos;
    return safeParse(pedido.productos, []);
  }, [pedido?.productos]);

  const envio = useMemo(() => pedido ? safeParse(pedido.envio, null) : null, [pedido?.envio]);
  const estadoNormalizado = (pedido?.estado || "").toString().trim().toLowerCase();

  // 🔄 SINCRONIZACIÓN DE PAGO EN TIEMPO REAL
  useEffect(() => {
    if (!pedido?.id) return;

    const idPedidoActual = String(pedido.id);

    // CASO 1: Notificación de Pusher
    if (pagoPendienteGlobal && String(pagoPendienteGlobal.pedido_id) === idPedidoActual) {
      setPagoPendiente({
        ...pagoPendienteGlobal,
        id: pagoPendienteGlobal.id || `pago-${Date.now()}`,
        imagen_pago: pagoPendienteGlobal.imagen_pago || pagoPendienteGlobal.imagen_nombre,
        requiereCarga: true
      });
    } 
    // CASO 2: Datos ya existentes en la base de datos (cPanel)
    else if (pedido.imagen_pago && !pagoPendiente) {
      setPagoPendiente({
        id: `db-${pedido.id}`,
        pedido_id: idPedidoActual,
        metodo: pedido.metodo_pago || 'pago móvil',
        imagen: pedido.imagen_pago, 
        imagen_pago: pedido.imagen_pago, 
        tiene_imagen: true,
        requiereCarga: true
      });
    }
  }, [pagoPendienteGlobal, pedido?.id, pedido?.imagen_pago]);

  // 🧹 LIMPIEZA AUTOMÁTICA AL CAMBIAR DE PEDIDO
  useEffect(() => {
    setPagoPendiente(null);
    setPrecioFinal("");
  }, [pedido?.id]);

  const confirmarPago = async () => {
    await enviarActualizacion('confirmado', { pago_confirmado: true });
    setPagoPendiente(null);
  };
  
  const manejarListo = async () => {
    await enviarActualizacion('listo');
  };

  const enviarActualizacion = async (nuevoEstado, extraData = {}) => {
    try {
      const tokenReal = user?.token;
      if (!tokenReal) {
        console.warn("⚠️ No hay token de autenticación disponible.");
        return;
      }

      // 🎯 SINCRO: Forzamos la URL directa pasando por index.php en producción
      const backendURL = 'https://clavelinacarnes.com/clavelina-backend-php/public/index.php/public/api/admin/actualizar_pedido';

      console.log(`🚀 Enviando actualización a ${backendURL}: ${nuevoEstado}`, extraData);

      const response = await fetch(backendURL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenReal}` 
        },
        body: JSON.stringify({
          pedido_id: pedido?.id,
          cliente_id: pedido?.cliente_id,
          estado: nuevoEstado,
          admin_id: user?.uuid || user?.id,
          ...extraData 
        })
      });
      
      const result = await response.json();

      if (response.ok) {
          console.log("✅ Servidor PHP respondió:", result);
          if (onPagoConfirmado) {
              onPagoConfirmado(); 
          }
      } else {
          console.error("❌ Error del servidor PHP:", result.error || "Error desconocido");
          alert("Error al actualizar: " + (result.error || "Consulta los logs del servidor"));
      }
    } catch (err) {
      console.error("❌ Error de conexión con el Backend PHP:", err);
      alert("No se pudo conectar con el servidor. Verifica tu conexión a internet.");
    }
  };

  const enviarPrecio = async () => {
    if (!precioFinal) return;
    await enviarActualizacion('esperando_pago', { precio_final: precioFinal });
    setPrecioFinal('');
  };

  // 🎨 CORREGIDO: Calcula el color de forma segura usando el ID numérico real
  function colorPorPedido(id) {
    const colores = ['border-primary', 'border-success', 'border-warning', 'border-danger', 'border-info'];
    const idNumerico = parseInt(id, 10);
    if (isNaN(idNumerico)) return 'border-secondary'; // Fallback seguro
    return colores[Math.abs(idNumerico) % colores.length];
  }

  if (!pedido || !pedido.id) {
    return <div className="text-white text-center p-3 small">Selecciona un pedido para ver los detalles.</div>;
  }

  return (
    <div className={`card shadow-sm bg-dark mb-3 border-start border-4 ${colorPorPedido(pedido.id)} animate__animated animate__fadeInUp`}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h5 className="text-white mb-0">
            Pedido de: <span className="text-danger">{pedido.cliente_nombre || pedido.cliente_id}</span>
          </h5>
          {pagoPendiente && (
            <span className="badge bg-warning text-dark animate__animated animate__heartBeat animate__infinite">
              💳 PAGO POR VERIFICAR
            </span>
          )}
        </div>

        <ul className="list-group mb-3">
          {Array.isArray(productos) && productos.map((p, i) => (
            <li key={i} className="list-group-item bg-dark text-white border-secondary py-1 small">
              <span className="text-danger fw-bold">{p.cantidad}</span> x {p.producto}
              {p.corte && <small className="text-info ms-2">({p.corte})</small>}
            </li>
          ))}
        </ul>

        {envio && (
          <div className="p-2 mb-3 bg-black bg-opacity-25 rounded border border-secondary small">
            <p className="text-white mb-1"><i className="bi bi-geo-alt text-danger me-2"></i>{envio.direccion}</p>
            <p className="text-white mb-0"><i className="bi bi-telephone text-success me-2"></i>{envio.telefono}</p>
          </div>
        )}

        <div className="row g-2">
          <div className="col-7">
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-secondary text-white border-0">$</span>
              <input
                type="number"
                className="form-control bg-dark text-white border-secondary"
                placeholder="Precio"
                value={precioFinal}
                onChange={e => setPrecioFinal(e.target.value)}
              />
              <button className="btn btn-danger" onClick={enviarPrecio}>OK</button>
            </div>
          </div>
          <div className="col-5">
            <button
              className={`btn btn-sm w-100 ${pedido.modo_entrega === 'retiro' ? 'btn-primary' : 'btn-success'}`}
              onClick={manejarListo}
              disabled={estadoNormalizado === 'completado'}
            >
              {pedido.modo_entrega === 'retiro' ? 'Listo p/Retiro' : 'Marcar Listo'}
            </button>
          </div>
        </div>

        {pagoPendiente && (
          <div className="mt-3 pt-3 border-top border-secondary animate__animated animate__fadeIn">
            <PagosView 
              filtroPedidoId={pedido.id}
              pagoDirecto={pagoPendiente} 
              key={`pago-${pedido.id}`} 
            />
            <button className="btn btn-success btn-sm w-100 mt-2 fw-bold" onClick={confirmarPago}>
              CONFIRMAR Y FINALIZAR PAGO
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PedidoCard;